import { createHOC } from 'vue-hoc';
import { get, concat } from 'lodash';

import auth from '../../../providers/auth';
import userManagement from '../../../providers/userManagement';
import withUser from '../../../hoc/withUser';

import NotificationList from './NotificationList.vue';

const options = {
  name: 'NotificationListContainer',  
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      state: {
        notifications: [],
        inviteNotifications: [],
      },
    };
  },
  async created() {
    this.fetchNotifications();
    this.fetchInvites();
  },  
  mounted() {
    this.$eventHub.$on('INVITES::REFRESH', () => {
      this.fetchInvites();
    });
  },
  methods: {
    async fetchInvites() {
      userManagement.get(`invites/user/${this.user.user_id}`).then(response => {
        this.state.inviteNotifications = 
          this.transformInvites(get(response, 'data.invites', []));
      });
    },
    transformInvites(invites) {
      return invites
        .filter(invite => !invite.dismissed && !invite.declined)
        .map(invite => {
          return {
            color: 'green',
            date: new Date(),
            message: `${invite.org_name} has invited you to their organization`,
            title: 'New Invitation',
            type: 'INFO',
            targets: [
              {
                principalId: this.user.user_id,
                principalType: 'USER'
              }
            ],
            actions: [
              {
                  label: 'Decline',
                  type: 'DISMISS_INVITE',
                  metadata: { invite },
              },            
              {
                label: 'Accept',
                type: 'ACCEPT_INVITE',
                metadata: { invite },
              },
            ],
          };
        });
    },
    async fetchNotifications() {
      userManagement.get('notifications/pending').then(response => {
        this.state.notifications = 
          this.transformNotifications(get(response, 'data.notifications', []));
      });
    },
    transformNotifications(notifications) {
      return notifications
        .map(notif => {
          if (notif.type !== 'POPUP_MESSAGE') {
            // Add dismiss action
            notif.actions.push({
              type: 'DISMISS',
              label: 'Dismiss',
            });  
          }
          return notif;
        });
    },
    async gotoCaseLink(action) {
      const caseId = action.metadata.caseId;        
      await this.$router.push({ name: 'cases', query: {caseId}}, () => {
        // this is here to supress redundant navigation warning
      });
      this.$store.commit('caseSelect/caseLoadId', caseId);
    },
    async dismiss(notificationId) {
      await userManagement.post(`notifications/${notificationId}/read`);
      this.fetchNotifications();
    },
    async unread(notificationId) {
      await userManagement.post(`notifications/${notificationId}/unread`);
      this.fetchNotifications();
    },
    async acceptInvite(notificationId, action) {
      const notification = this.state.inviteNotifications.find(n => n._id === notificationId);
      if (!notification) {
        return;
      }
      // Add some status properties to the notification in reactive way
      this.$set(notification, 'loading', true);
      this.$set(notification, 'error', null);

      try {
        notification.loading = true;

        // Finish invitation
        const invite = action.metadata.invite;
        await userManagement.post(
          '/invitations/finish-invite', 
          { inviteId: invite._id, email: invite.email }
        );

        // Log into org
        await userManagement.post('/auth/changeOrg', {
          orgId: invite.org_id,
        });
        await auth.renewSession();
        window.location.reload();
      } catch (e) {
        notification.error = e;
      } finally {
        notification.loading = false;
      }
    },
    async dismissInvite(notificationId, action) {
      const notification = this.state.inviteNotifications.find(n => n._id === notificationId);
      if (!notification) {
        return;
      }
      // Add some status properties to the notification in reactive way
      this.$set(notification, 'loading', true);
      this.$set(notification, 'error', null);

      const inviteId = action.metadata.invite._id;
      try {
        await userManagement.post(`invites/dismiss/${inviteId}`);
        this.state.inviteNotifications = this.state.inviteNotifications.filter(n => {
          n._id !== notification._id
        });
      } catch (e) {
        notification.error = e.error;
      } finally {
        notification.loading = false;
      }     
    },
  },
};

const renderWith = {
  props: {
    notifications() {
      return concat(this.state.notifications, this.state.inviteNotifications);
    },
    loading() {
      return this.state.loading;
    },
    error() {
      return this.state.error;
    },
  },
  listeners: {
    async action(notificationId, action, callback) {
      if (action.type === 'CASE_LINK') {
        this.gotoCaseLink(action);
      } else if (action.type === 'DISMISS') {
        this.dismiss(notificationId);
      } else if (action.type === 'ACCEPT_INVITE') {
        this.acceptInvite(notificationId, action);
      } else if (action.type === 'DISMISS_INVITE') {
        this.dismissInvite(notificationId, action);
      } else if (action.type === 'EVENT_HUB') {
        this.$eventHub.$emit(action.metadata.event, ...action.metadata.args, callback);
      }
    },
    async dismiss(notificationId) {
      this.dismiss(notificationId);
    },
    async unread(notificationId) {
      this.unread(notificationId);
    },
  },
};

const NotificationListContainer = withUser()(createHOC(NotificationList, options, renderWith));
export default NotificationListContainer;
